import $ from 'jquery';
import moment from 'moment';
import storyCardTemplate from '../templates/stories/storyCard.hbs';
import {utf8ToHtmlEntities} from '../util/text';
import {history as Bhistory, Model} from 'backbone';
import {messageHandler} from './sinclair.uiTools';
import {beforeMainLoader, completeMainLoader, getPostJSONOpts, hideTopLoader, showTopLoader, ajaxRequest} from './sinclair.ajax';
import {
    getActiveFilters,
    getBiosFilters,
    getPackageTypeFilterValue,
    getStatusFilterValue,
    getStationFilterValue,
    getMyMediaFilterValue,
    getDateObj,
} from '@sinclair-digital/sbg-storyline-components/src/components/PackageSearch/selectors';
import {setUrlSearchQuery} from '@sinclair-digital/sbg-storyline-components/src/components/PackageSearch/url';
import {always,append,compose,flatten,has,isEmpty,map,pipe,when} from 'ramda';
import {getFeatureFlag} from '@sinclair-digital/sbg-storyline-sdk/lib/storyline/featureFlags';

//:: String | Date | null -> String | null
const formatDate = maybeDate => (maybeDate ? moment(maybeDate).format('YYYY-MM-DD') : null);
const myMediaFilterEnabled = () => getFeatureFlag('myMediaFilter');


export const makeSingleAndClause = clause => ({
    andClauses: [clause],
});

export const makeTypeClause = type => makeSingleAndClause({type});

export const makeAlertTypeClause = alertTypes =>
    makeSingleAndClause({
        type: ['ALERT'],
        indexSearchTerms: {
            alert_type_url: alertTypes,
        },
    });

export const appendTypes = (types, alertTypes) => {
    const hasAlerts = types.includes('ALERT');
    const typeFiltersWithoutAlerts = types.filter(type => type !== 'ALERT');

    return pipe(
        when(always(hasAlerts), append(makeAlertTypeClause(alertTypes))),
        when(always(typeFiltersWithoutAlerts.length > 0), append(makeTypeClause(typeFiltersWithoutAlerts)))
    );
};

export const addTextSearch = (searchFields, searchText) => {
    const escapedSearchText = searchText.map(utf8ToHtmlEntities);

    if (searchFields.length > 0) {
        const searchFieldObjects = searchFields.map(searchField => ({
            indexSearchTerms: {
                [searchField]: escapedSearchText,
            },
        }));

        return compose(
            flatten,
            map(({andClauses}) =>
                searchFieldObjects.map(sfo => ({
                    andClauses: [...andClauses, sfo],
                })))
        );
    }

    return map(({andClauses}) =>
        ({andClauses: andClauses.map(clause => ({...clause, textMatches: escapedSearchText}))})
    );
};

export const addCommonAndClause = commonAndClause =>
    map(({andClauses}) =>
        ({andClauses: andClauses.map(clause => ({...commonAndClause, ...clause}))})
    );

export const addStatuses = statuses =>
    when(
        always(!isEmpty(statuses)),
        map(({andClauses}) => ({
            andClauses: [...andClauses, {indexSearchTerms: statuses}],
        }))
    );

export const addBios = bios =>
    when(
        always(!isEmpty(bios)),
        map(({andClauses}) => ({
            andClauses: [...andClauses, {indexSearchTerms: {
                author_uuid: bios,
            }}],
        }))
    );

export const addMyMediaFilter = myMediaFilter =>
    when(
        always(!isEmpty(myMediaFilter)),
        map(({andClauses}) => ({
            andClauses: andClauses.map((clause, index) =>
                index === 0
                    ? {
                        ...clause,
                        ...myMediaFilter[0],
                    }
                    : clause
            ),
        }))
    );

export const collectionMixins = {
    search: {
        CHUNK_SIZE: 50,
        setSearchType(type) {
            this.searchType = type;
        },
        setSort(val) {
            this.searchParameters.orderBy = val;

            return this;
        },
        setPackageTypes(val) {
            this.modifyCommonAndClause({type: val});

            return this;
        },
        setPackageProperties(val) {
            this.modifyCommonAndClause({property: val});

            return this;
        },
        setQuery({
            searchText,
            typeFilter,
            stationFilter,
            myMediaFilter = null,
            bios,
            sort,
            dates,
            alertTypes = [],
            statuses = {},
            searchFields = [],
        }) {
            const count = this.limit || 1000;

            // If no dateThru is set, CloudSearch will apply a default date range
            if (!dates.dateThru) {
                dates.dateThru = moment()
                    .utc()
                    .toISOString();
            }

            const commonAndClause = {
                property: stationFilter,
                dateFrom: dates.dateFrom,
                dateThru: dates.dateThru,
            };

            const orClauses = pipe(
                appendTypes(typeFilter, alertTypes),
                addCommonAndClause(commonAndClause),
                addTextSearch(searchFields, searchText),
                addStatuses(statuses),
                addBios(bios),
                myMediaFilter ? addMyMediaFilter(myMediaFilter) : (data) => data,
            )([]);

            this.searchParameters = {
                offset: 0,
                count,
                orderBy: sort,
                orClauses,
            };
        },
        searchInitialize({transformSummaries} = {}) {
            const count = this.limit || 1000;

            if (transformSummaries) {
                this._transformSummaries = transformSummaries;
            }

            this.searchParameters = {
                commonAndClause: {
                    type: [],
                    property: this.userModel
                        .get('availableTargets')
                        .toJSON()
                        .map(target => target.site.name),
                    dateFrom: null,
                    dateThru: moment()
                        .utc()
                        .toISOString(),
                    textMatches: null,
                },
                orClauses: [
                    {
                        andClauses: [],
                    },
                ],
                orderBy: 'ascendingDate',
                count,
                offset: 0,
            };
        },
        _transformSummaries(summaries) {
            return summaries;
        },
        getSearchResults() {
            return this._fetchUuids()
                .then(this._setCurrentUuids)
                .then(this._getNextChunk)
                .then(this._fetchSummaries)
                .then(this._transformSummaries)
                .then(packages => {
                    this.reset(packages);

                    return packages;
                });
        },
        getMoreSummaries() {
            return Promise.resolve(this._availablePackages)
                .then(this._getNextChunk)
                .then(this._fetchSummaries)
                .then(this._transformSummaries)
                .then(summaries => {
                    this.add(summaries);

                    return summaries;
                });
        },
        _fetchUuids() {
            const searchParameters = this.searchParameters;
            const opts = getPostJSONOpts({
                url: `api/rest/search/${this.searchType}`,
                data: JSON.stringify(searchParameters),
            });

            return new Promise((resolve, reject) => {
                opts.success = resolve;
                opts.error = reject;

                return (this.fetchXhr = ajaxRequest(opts));
            });
        },
        _setCurrentUuids(uuidArray) {
            this._availablePackages = uuidArray;
            return this._availablePackages;
        },
        _getNextChunk(uuidArray) {
            this.pendingSummaryFetch = uuidArray.splice(0, this.CHUNK_SIZE);

            return this.pendingSummaryFetch;
        },
        _fetchSummaries(uuidArray) {
            const opts = getPostJSONOpts({
                url: `api/rest/${this.searchType}/summary`,
                data: JSON.stringify(uuidArray),
            });
            if (uuidArray.length) {
                return new Promise((resolve, reject) => {
                    opts.success = resolve;
                    opts.error = reject;

                    return (this.fetchXhr = ajaxRequest(opts));
                });
            } else {
                return Promise.resolve([]);
            }
        },
        fetchIsPending() {
            const fetchXhr = this.fetchXhr;

            return fetchXhr && fetchXhr.readyState > 0 && fetchXhr.readyState < 4;
        },
        abortSearch() {
            hideTopLoader();

            return this.fetchXhr.abort();
        },
        modifyCommonAndClause(queryObject) {
            this.searchParameters.offset = 0;
            this.searchParameters.commonAndClause = Object.assign(this.searchParameters.commonAndClause, queryObject);
            return this;
        },
        morePackagesAvailable() {
            return this._availablePackages.length;
        },
    },
};

export const viewMixins = {
    basicSearch: {
        _scrollThreshold: 500,
        _$packageList: '',
        _$lastPackage: $(''),
        resetPackageList(packages) {
            this._$packageList.empty();
            this.appendPackages(packages);

            return packages;
        },
        packageJsonToHtml(json) {
            if (!this.cardTemplate) {
                this.cardTemplate = storyCardTemplate;
            }

            return this.cardTemplate(json);
        },
        appendPackages(packages) {
            const newPackages = packages.map(pkg => $(this.packageJsonToHtml(pkg)).sdaCard());
            this._$packageList.append(newPackages);
        },
        _getSearchText(query) {
            return [query.searchText.text];
        },
        _sendQuery() {
            const query = this.searchOptions.toJSON();
            const searchText = this._getSearchText(query);
            const typeFilter = getPackageTypeFilterValue(query);
            const stationFilter = getStationFilterValue(query);
            const sort = this.sortView.getSortValue();
            const dates = getDateObj(query);
            const statuses = getStatusFilterValue(query);
            const alertTypes = query.alertType;
            const searchFields = getActiveFilters(query.field.filters);
            const sortOrder = this.sortView.getSortDirection();
            const sortBy = this.sortView.getSortColumn();
            const bios = getBiosFilters(query?.content?.filters || []);
            const queryToPass = {
                searchText, typeFilter, stationFilter, bios, sort, dates, statuses, alertTypes, searchFields,
            };
            if (myMediaFilterEnabled()) {
                queryToPass.myMediaFilter = getMyMediaFilterValue(query);
            }
            this.collection.setQuery(queryToPass);
            if (!this.searchPersistenceOff) {
                setUrlSearchQuery({...query, sortOrder, sortBy});
            }
            this.newSearch();
        },
        newSearch() {
            this._$packageList = this.$('.package-list');
            this._$scrollContainer.off('scroll', this._checkScrollPosition);
            this._$scrollContainer.on('scroll', this._checkScrollPosition);
            this.historyFragment = Bhistory.fragment;
            this._$packageList.empty();

            if (this.collection.fetchIsPending()) {
                this.collection.abortSearch();
            }

            beforeMainLoader();

            return this.collection
                .getSearchResults()
                .then(this.resetPackageList)
                .then(this._updateLazyLoadBindings)
                .then(completeMainLoader)
                .catch(this.searchErrorHandler);
        },
        searchErrorHandler(error) {
            if (error.statusText !== 'abort') {
                completeMainLoader();
            }

            if (has(error, 'getAllResponseHeaders') && error.getAllResponseHeaders()) {
                hideTopLoader();
                messageHandler('error', 'There was an error loading packages.');
            }

            return this;
        },

        //region LAZY LOAD
        _updateLazyLoadBindings() {
            this._$lastPackage = this._$packageList.find('article:last');
            this._checkScrollPosition();

            if (!this._$lastPackage.length && !this.collection.morePackagesAvailable()) {
                this._$packageList.text('No packages found.');
            }

            return this;
        },
        _checkScrollPosition() {
            const offset = this._$lastPackage.offset(); // returns null if doesn't exist

            // check position of last article to see if it's in or close to the viewport bottom
            if (this.collection.morePackagesAvailable() && this.collection.fetchXhr.readyState === 4) {
                if (!offset || offset.top - this._scrollThreshold <= window.innerHeight) {
                    this._loadMoreSummaries();
                } else {
                    hideTopLoader();
                }
            } else if (!this.collection.morePackagesAvailable()) {
                hideTopLoader();
            }
        },
        _loadMoreSummaries() {
            if (this.collection.morePackagesAvailable() && Bhistory.fragment === this.historyFragment) {
                showTopLoader();

                return this.collection
                    .getMoreSummaries()
                    .then(this.appendPackages)
                    .then(this._updateLazyLoadBindings)
                    .catch(this.searchErrorHandler);
            } else {
                if (!this._$packageList.children().length) {
                    this._$packageList.html('No packages found');
                }
                return Promise.reject(new Error('No packages found'));
            }
        },
        //endregion LAZY LOAD
    },
    openClose: {
        open() {
            this.$el.addClass('panel-open');
        },
        close() {
            this.$el.removeClass('panel-open');
        },
    },
};

export const modelMixins = {
    apiDestroyVersion: {
        destroy() {
            const version = this.get('version');
            let url = this.url();

            if (version) {
                url += '/' + version;
            }

            return Model.prototype.destroy.call(this, {
                url,
                dataType: 'text',
            });
        },
    },
    getStoryListModelJSON: {
        getStoryListModelJSON() {
            const json = this.toJSON();
            const isAlert = json.type !== 'story' && json.type !== 'bio';

            return {
                alertType: isAlert ? json.type : null,
                byLine: json.byLine,
                expiresDateTime: formatDate(json.expiresDateTime),
                expiresDateTimeLocal: formatDate(json.expiresDateTimeLocal),
                headline: json.headline || json.title,
                imageCount: json.images ? json.images.length : null,
                images: json.images,
                primaryTargetName: json.primaryTarget.site.name,
                primaryTargetUuid: json.primaryTarget.site.uuid,
                publishAuthorizationDateTime: formatDate(json.publishAuthorizationDateTime),
                publishedDateTime: formatDate(json.publishedDateTime),
                publishedDateTimeLocal: formatDate(json.publishedDateTimeLocal),
                savedDateTime: formatDate(json.savedDateTime),
                savedDateTimeLocal: formatDate(json.savedDateTimeLocal),
                scheduledDateTime: formatDate(json.scheduledDateTime),
                scheduledDateTimeLocal: formatDate(json.scheduledDateTimeLocal),
                status: json.status,
                summary: json.summary,
                terminatedDateTime: formatDate(json.terminatedDateTime),
                terminatedDateTimeLocal: formatDate(json.terminatedDateTimeLocal),
                thumbnail: json.images && json.images.length ? json.images[0] : null,
                type: json.type,
                unpublishAuthorizationDateTime: formatDate(json.unpublishAuthorizationDateTime),
                uuid: json.uuid,
                version: json.version,
                versionStatus: json.versionStatus,
                videoCount: json.videos ? json.videos.length : null,
                videos: json.videos,
            };
        },
    },
};
